<template>
  <app-race-video-page
    :lockOrientation="lockOrientation"
    :championshipMasterId="championshipMasterId"
    :raceId="raceId"
    :highlightId="highlightId"
    :movieStartActualTime="movieStartActualTime"
    :driverId="driverId"
    :hasSubVideo="true"
    :hasVolumeControl="true"
  />
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import AppRaceVideoPage, { LockOrientationType } from '@/views/RaceVideoPage/RaceVideoPage.vue'

/**
 * レース動画再生+テレメトリーページ
 */
export default defineComponent({
  name: 'RaceVideoPage',
  components: {
    AppRaceVideoPage,
  },
  props: {
    /**
     * 画面固定
     */
    lockOrientation: {
      type: String as PropType<LockOrientationType>,
      default: 'landscape',
    },
    championshipMasterId: {
      type: String,
      required: true,
    },
    raceId: {
      type: String,
      required: true,
    },
    highlightId: {
      type: String,
      required: false,
    },
    /**
     * 動画再生時に初期設定する再生位置の実時間。UnixTime(ミリ秒)
     */
    movieStartActualTime: {
      type: Number,
      required: false,
    },
    /**
     * 動画再生時に初期設定するドライバーのID
     */
    driverId: {
      type: String,
      required: false,
    },
  },
})
</script>

<style lang="scss">
.page-wrapper .race-video-page {
  $dataViewAreaWidth: 375px;
  $minVideoHeight: 506px;
  $subVideoWrapperHeight: 220px;

  min-width: $browserMinWidth;
  min-height: $minVideoHeight;
  overflow-y: auto;

  &--landscape-primary,
  &--landscape-secondary {
    .race-video-page__data {
      width: $dataViewAreaWidth;
      height: auto;
      overflow: unset;

      // テレメトリー
      .race-video-data-view-pane {
        position: relative;
        height: calc(100vh - #{$subVideoWrapperHeight});
        min-height: 376px;

        &__telemetry {
          width: 100%;
          min-height: 332px;
          max-height: calc(100vh - #{$subVideoWrapperHeight} - 44px);
          overflow-y: auto;
        }

        .race-telemetry-section {
          font-size: 10px;
          width: min(100%, 32.3em);
          height: calc(#{var(--screen-height)} - #{$subVideoWrapperHeight} - 50px);
          min-height: 452px;
          padding: min(4.2667vw, 1em);
        }

        .telemetry-rpm-gauge-parts,
        .telemetry-brk-press-fr-parts,
        .telemetry-throttle-pedal-parts,
        .telemetry-gear-pos-parts,
        .telemetry-car-speed-parts {
          width: 27em;
        }

        .telemetry-rpm-parts {
          left: calc(min(4.2667vw, 1.333em) + 2.6667em);
        }
      }

      // ランキング
      .race-video-ranking-pane {
        position: relative;

        .ranking-setting-section {
          &__inner {
            padding: 0 20px;
            gap: 18px;
          }

          &__data-select {
            width: calc(100% - 126px);
          }
        }

        .race-video-ranking-pane__list {
          max-width: 100%;
          height: calc(100vh - #{$subVideoWrapperHeight} - 88px);
          min-height: calc(#{$browserMinHeight} - #{$subVideoWrapperHeight} - 88px);
        }

        .ranking-list-section {
          padding: 6px 0 6px 12px;
          font-size: 10px;
        }

        .ranking-player-parts {
          width: min(100%, 344px);
          font-size: 10px;
          padding: 0 4px 10px 0;

          &__rank {
            width: 28px;
            margin-right: 2px;
            padding-left: 0;

            span {
              font-size: 1.5em;
            }
          }

          &__photo {
            width: 4em;
            height: 4em;
            margin-right: 8px;
          }

          &__radio {
            gap: 5px;

            &-icon {
              width: 1.71em;
            }

            &__gauge {
              width: 32px;
              height: 2px;

              &-meter {
                height: 2px;
              }
            }

            &--playing {
              .ranking-player-parts__radio-icon {
                width: 2.57em;
              }
            }
          }

          &__data {
            width: 114px;
            margin-right: 8px;
          }

          &__tags {
            gap: 0.5em;

            &-item {
              padding: 0 0.2em;
              height: 1.6em;
              font-size: 10px;
              border-radius: 0.625em;

              &-lp {
                display: none;
              }

              &-ls {
                display: block;
                transform: scale(0.9);
              }
            }

            &-count {
              font-size: 10px;
              transform: translate(50%, -50%) scale(0.8);
            }
          }

          &__name {
            height: 2em;
            line-height: 2;
            font-size: 0.83em;
            text-overflow: ellipsis;
            overflow: hidden;

            .name {
              display: initial;
            }

            .abbreviation {
              display: none;
            }
          }

          &__ots {
            height: initial;
          }

          &__select-data {
            width: 140px;
            height: 4em;
            gap: 0.41667em 0.2em;
            padding: 0.667em 0.4em;
            border-radius: 0.667em;

            &__heading {
              width: 100%;
              font-size: 0.833em;
              border-radius: 0.2em;
            }

            &__time {
              width: 60px;
              font-size: 10px;
            }

            &__separator {
              width: 6px;
            }
          }

          &__under-line {
            bottom: 5px;
            right: 0;
            left: auto;
            width: 100%;
          }
        }
      }

      // ハイライト
      .race-video-highlights-pane {
        position: relative;
        height: auto;

        .highlight-header-section {
          padding: 0 15px;

          &__title {
            display: initial;
          }

          &__filter {
            width: 184px;
          }
        }

        .highlight-contents-section {
          height: calc(100vh - #{$subVideoWrapperHeight} - 44px);
          min-height: calc(#{$browserMinHeight} - #{$subVideoWrapperHeight} - 44px);
        }
      }

      // 無線一覧
      .race-video-radio-pane {
        position: relative;

        .radio-list-section {
          max-width: none;
          height: calc(100vh - #{$subVideoWrapperHeight} - 44px);
          min-height: calc(#{$browserMinHeight} - #{$subVideoWrapperHeight} - 44px);
        }

        .radio-header-section {
          padding: 0 15px;

          &__title {
            display: initial;
          }

          &__filter {
            width: 213px;
          }
        }

        .radio-list-item-parts {
          max-width: 326px;
          margin-left: auto;
          margin-right: auto;

          &__video-scene-select {
            &__img {
              width: 32px;
            }

            &__information {
              width: calc(100% - 70px);
            }
          }
        }
      }

      // GPS
      .race-video-page__gps {
        position: relative;
        height: calc(100vh - #{$subVideoWrapperHeight});
        min-height: calc(#{$browserMinHeight} - #{$subVideoWrapperHeight});
      }

      .race-video-gps-pane__gps-content {
        padding-bottom: 24px;
      }

      .gps-mode-selector-section--landscape-primary {
        width: calc(100% - 48px);

        .gps-mode-selector-section__button {
          font-size: 12px;
        }
      }

      // 選手選択部
      .race-player-info-section {
        &__inner {
          padding: 0 24px;
        }

        &__button-set {
          gap: 15px;
        }
      }

      // 選手選択モーダル
      .player-select-modal-section {
        position: absolute;
        z-index: 25;

        .modal__wrapper {
          position: absolute;
          top: 52px;
          left: 8px;
          width: calc(100% - 16px);
        }

        .modal__content {
          height: calc(100vh - #{$subVideoWrapperHeight} - 120px);
        }
      }

      // 各フィルターリスト
      .filter-selector-parts__list {
        top: 40px;
        right: 5px;
        width: 240px;
        max-height: calc(100vh - #{$subVideoWrapperHeight} - 44px);
      }

      // レース選択・無線選択モーダル
      .radio-select-section,
      .race-select-section {
        top: 59px;
        bottom: auto;
        height: calc(100vh - #{$subVideoWrapperHeight} - 59px);
        min-height: 322px;

        &__content {
          width: calc(100% - 68px);
          height: calc(100vh - 279px);
          min-height: 322px;
        }
      }
    }

    .race-video-pane {
      &__overlay-trigger {
        min-height: 100vh;
      }

      &__blank_logo {
        width: 120px;
        top: 24px;
        right: 30px;
        left: auto;
        transform: none;
        z-index: 2;
      }

      .circuit-map-section {
        &__car {
          width: 76px;
        }
      }

      &--landscape-primary,
      &--landscape-secondary {
        &.view-overlay {
          .race-video-pane {
            &__information {
              right: 360px;

              .race-video-page--radio &,
              .race-video-page--ranking &,
              .race-video-page--highlight &,
              .race-video-page--telemetry &,
              .race-video-page--gps & {
                right: 360px;
              }
            }
          }
        }

        .race-video-pane {
          &__information {
            right: 360px;

            .race-video-page--radio &,
            .race-video-page--ranking &,
            .race-video-page--highlight &,
            .race-video-page--telemetry &,
            .race-video-page--gps & {
              right: 360px;
            }
          }
        }
      }
    }

    .race-video-page__video {
      .video-player-section--landscape-primary,
      .video-player-section--landscape-secondary {
        .sfgo-video-dimensions {
          position: unset;
          top: unset;
          left: unset;
          transform: unset;
        }
      }
    }

    // オーバレイセクション
    .video-overlay-section {
      position: fixed;
      top: 0;
      left: 0;
      right: auto;
      bottom: auto;
      width: 100vw;
      height: 100vh;
      transform: none;

      // ビデオタイトル部
      .overlay-header-parts {
        &--landscape-primary,
        &--landscape-secondary {
          justify-content: flex-start;

          .overlay-header-parts {
            &__inner {
              width: calc(100% - #{$dataViewAreaWidth});
              padding: 24px 8px;
              height: auto;
            }

            &__title {
              font-size: 28px;
            }

            &__back-button {
              width: 48px;
              height: 48px;
              margin-right: 8px;

              img {
                width: 36px;
                height: 36px;
              }
            }
          }
        }
      }

      // データビューセレクター
      .landscape-data-view-selector-parts {
        right: 12px;
        width: 356px;
        padding: 8px;
        gap: 8px;

        &__button {
          flex: 1;
          width: auto;

          svg {
            width: 24px;
          }
        }
      }

      // ビデオコントローラー
      .overlay-controller-parts {
        transform: none;

        &::before {
          width: max(100vw, #{$browserMinWidth});
        }

        &__inner {
          height: 78px;
        }

        @media screen and (max-width: 920px) {
          left: 20px;
          width: calc(100% - 40px);
          max-width: calc(100% - 40px);
          padding-right: 0;

          .overlay-controller-parts {
            &__angle-switcher {
              left: 0;
            }

            &__live {
              margin-left: 0;
            }

            &__volume-controller {
              display: none;
            }
          }
        }

        @media screen and (max-width: 820px) {
          .overlay-controller-parts {
            &__button {
              &__prev5s,
              &__next30s,
              &__play,
              &__pause,
              &__next5s {
                margin-left: 6px;
              }
            }
          }
        }

        @media screen and (max-width: 740px) and (min-width: 700px) {
          .overlay-controller-parts {
            &__buttons {
              margin-left: 20px;
            }
          }
        }

        @media screen and (max-width: 700px) {
          .overlay-controller-parts {
            &__angle-switcher,
            &__view-mode,
            &__live,
            &__button__slow {
              display: none;
            }

            &__volume-controller {
              left: 0;
            }
          }
        }
      }

      // サーキットマップ
      .circuit-map-section {
        &__mode-tag {
          right: 20px;
          bottom: calc(26px + $CIRCUIT_MODE_MAP_ADJUSTMENT_Y);
          max-width: 240px;
        }
      }
    }

    // シングルモード以外位置指定
    &:not(.race-video-page--single) {
      align-items: flex-start;
      min-height: $browserMinHeight;

      .race-video-page__video {
        display: flex;
        flex-flow: row wrap;
        align-items: center;
        justify-content: center;
        width: calc(100% - #{$dataViewAreaWidth});
        min-height: 100vh;
        padding-top: 0;

        .video-player-section--landscape-primary,
        .video-player-section--landscape-secondary {
          position: relative;
        }
      }

      .video-overlay-section {
        @media screen and (max-width: $browserMinWidth) {
          overflow: auto;
          width: 100%;
        }
      }
    }

    // 動画のみモード
    &.race-video-page--single {
      max-width: none;
      min-height: $minVideoHeight;

      .race-video-page__video {
        padding-top: 0;
        min-width: 900px;
        height: max(100vh, #{$minVideoHeight});

        .video-player-section--landscape-primary,
        .video-player-section--landscape-secondary {
          max-width: none;
        }
      }

      .race-video-pane {
        &--landscape-primary,
        &--landscape-secondary {
          .race-video-pane__information {
            right: 0;
          }

          &.view-overlay {
            .race-video-pane__information {
              right: 360px;
            }
          }
        }
      }
    }

    // フルスクリーン指定
    &.race-video-page--full-scale {
      width: 100% !important;

      .race-video-page__video {
        overflow: hidden;

        .sfgo-video-dimensions {
          video {
            width: max(100vw, calc(max(100vh, 600px) / 9 * 16));
            height: max(100vh, calc(max(100vw, 900px) / 16 * 9));
            top: 50%;
            left: 50%;
            right: auto;
            bottom: auto;
            transform: translate(-50%, -50%);
          }
        }
      }
    }

    // ハイライトシーン
    &.race-video-page--highlight {
      position: relative;
    }

    .race-video-page__highlights {
      .race-video-highlights-pane__modal {
        position: fixed;
        left: auto;
        width: 375px;

        @media screen and (max-width: $browserMinWidth) {
          left: calc($browserMinWidth - 375px);
          right: auto;
        }
      }

      .highlight-contents-section {
        padding-bottom: 70px;
      }

      .highlight-comment-section {
        &--landscape-primary,
        &--landscape-secondary {
          .highlight-comment-section__modal {
            width: 375px;

            @media screen and (max-width: $browserMinWidth) {
              left: calc($browserMinWidth - 375px);
              right: auto;
            }
          }
        }
      }

      .highlight-item-section {
        &--landscape-primary,
        &--landscape-secondary {
          .highlight-item-section__modal {
            width: 375px;

            @media screen and (max-width: $browserMinWidth) {
              left: calc($browserMinWidth - 375px);
              right: auto;
            }
          }

          .comment-input-section {
            .comment-input-section__modal {
              width: 375px;

              @media screen and (max-width: $browserMinWidth) {
                left: calc($browserMinWidth - 375px);
                right: auto;
              }
            }
          }
        }
      }

      // ハイライトカード作成
      .new-highlights-section {
        position: absolute;
        width: 375px;
        bottom: 0;

        &--edit {
          height: calc(100vh - 264px);
          min-height: calc(#{$browserMinHeight} - 264px);
          overflow: auto;
          padding-top: 3px;
        }

        &--landscape-primary,
        &--landscape-secondary {
          display: block;
        }

        @media screen and (max-width: 900px) {
          right: auto;
          left: 525px;
        }

        &__mask {
          display: none;
        }

        &__add-button {
          bottom: 12px;
        }
      }

      .new-highlights-input-section {
        transition: none;

        .modal {
          transition: none;
        }

        &--landscape-primary,
        &--landscape-secondary {
          .highlights-card-parts {
            width: calc(100% - 16px);
          }
        }

        &__content {
          min-height: calc(100vh - 312px);
        }

        .comment-input-section__modal {
          width: 375px;
          left: auto;
        }
      }
    }
  }
}
</style>
